import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO
        title="Forgot Password"
        description="Reset your RED.FLAG account password. Enter your email to receive a secure reset link within 1 hour."
        path="/forgot-password"
      />
      <div className="bg-black text-white p-10 flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[var(--alert-red)] flex items-center justify-center font-display font-black">!</div>
          <span className="font-display font-black tracking-tighter text-xl">RED.FLAG</span>
        </div>
        <div>
          <div className="overline text-white/60 mb-4">PASSWORD RESET</div>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tighter leading-[0.9]">
            FORGOT IT? <br />
            <span className="text-[var(--alert-red)]">NO BIG DEAL.</span>
          </h1>
          <p className="mt-6 font-mono text-sm max-w-md text-white/70">
            Enter your email. We&apos;ll send a one-time link to reset your password. Link expires in 1 hour.
          </p>
        </div>
        <div className="overline text-white/40">ZERO QUESTIONS ASKED.</div>
      </div>

      <div className="flex items-center justify-center p-8 bg-white">
        {sent ? (
          <div className="w-full max-w-md text-center" data-testid="forgot-success">
            <div className="overline mb-2">CHECK YOUR INBOX</div>
            <h2 className="font-display font-black text-4xl tracking-tighter mb-4">Email sent.</h2>
            <p className="font-mono text-sm text-[var(--text-secondary)] mb-6">
              If <strong>{email}</strong> is registered, a reset link is on its way. It expires in 1 hour.
            </p>
            <Link to="/login" data-testid="back-to-login" className="inline-block w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors">
              BACK TO SIGN IN →
            </Link>
            <button
              data-testid="resend-link"
              onClick={() => setSent(false)}
              className="mt-3 w-full border-2 border-black py-3 font-display font-bold hover:bg-black hover:text-white transition-colors"
            >
              SEND ANOTHER
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md" data-testid="forgot-form">
            <div className="overline mb-2">FORGOT PASSWORD</div>
            <h2 className="font-display font-black text-4xl tracking-tighter mb-8">Send me a link.</h2>

            <label className="block mb-6">
              <div className="overline mb-2">EMAIL</div>
              <input
                data-testid="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono"
              />
            </label>

            <button
              data-testid="forgot-submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors disabled:opacity-50"
            >
              {loading ? "SENDING…" : "SEND RESET LINK →"}
            </button>

            <div className="mt-6 font-mono text-sm">
              Remembered it?{" "}
              <Link to="/login" data-testid="link-to-login" className="underline font-bold">SIGN IN</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
